import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = {
  for: "level",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_4 = {
  for: "users",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_5 = { class: "flex flex-col justify-end pb-1.5" }
const _hoisted_6 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_7 = {
  key: 0,
  class: "text-danger bg-danger-100 p-2 text-sm shadow"
}
const _hoisted_8 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_9 = {
  controls: "",
  class: "w-full"
}
const _hoisted_10 = ["src"]

import { computed, reactive, ref, Ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { goBack } from '@/composables/useNavigation';
import { useUiStore } from '@/stores';
import { QueueService, SelectOptionsService, VaultService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { getAudioFormat } from '@/utils/audio';

import {
    FormAction,
    MultiSelectOption,
    Permission,
    QueueFormState,
    QueueModel,
    QueueStrategy,
    SelectOptionsServiceGetUsersResult,
} from '@/types';

import { AxiosResponse } from 'axios';


export default /*@__PURE__*/_defineComponent({
  __name: 'QueueFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const props = __props;

const route = useRoute();
const uiStore = useUiStore();
const { t } = useI18n();

const formState = reactive<QueueFormState>({
    name: '',
    strategy: '',
    users: [],
    fileAudio: '',
    isActive: true,
});

const file = ref<File | null>(null);
const oldFileName = ref<string | null>(null);
const audioRemoved = ref(false);
const audioPreview = ref<string | null>(null);
const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);
const strategyOptions: Ref<MultiSelectOption[]> = ref([
    { label: t(`queue.strategy.${QueueStrategy.RANDOM}`), value: QueueStrategy.RANDOM },
    { label: t(`queue.strategy.${QueueStrategy.RRORDERED}`), value: QueueStrategy.RRORDERED },
    { label: t(`queue.strategy.${QueueStrategy.RRMEMORY}`), value: QueueStrategy.RRMEMORY },
    { label: t(`queue.strategy.${QueueStrategy.RINGALL}`), value: QueueStrategy.RINGALL },
    { label: t(`queue.strategy.${QueueStrategy.FEWESTCALLS}`), value: QueueStrategy.FEWESTCALLS },
    { label: t(`queue.strategy.${QueueStrategy.LEASTRECENT}`), value: QueueStrategy.LEASTRECENT },
]);

const rules = computed<ValidationArgs<QueueFormState>>(() => ({
    name: { required, maxLength: maxLength(50) },
    strategy: { required },
    users: {},
    fileAudio: {},
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<QueueFormState>>> = useVuelidate(rules, formState);

watch([file], () => {
    if (file.value) {
        formState.fileAudio = file.value.name;
        audioPreview.value = URL.createObjectURL(file.value);
    } else {
        formState.fileAudio = '';
        audioPreview.value = null;
        audioRemoved.value = oldFileName.value !== null;
    }
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                has_extension: true,
                is_active: true,
            },
        });
        usersOptions.value = data;
    } catch (e) {
        toastServiceError(e);
    }
};

const fetchQueue = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await QueueService.get<QueueModel>(id);
        formState.name = data.name;
        formState.strategy = data.strategy;
        formState.users = (data.users || []).map((u) => u.id);
        formState.fileAudio = data.fileAudio;
        oldFileName.value = data.fileAudio;
        formState.isActive = data.isActive;
        await loadAudio();
    } catch (e) {
        toastServiceError(e);
    } finally {
        setLoading(false);
    }
};

const loadAudio = async () => {
    if (formState.fileAudio) {
        try {
            const { data } = await VaultService.getAudioAsset(formState.fileAudio);
            const blob = new Blob([data], { type: getAudioFormat(formState.fileAudio) });
            file.value = new File([blob], formState.fileAudio, {
                type: blob.type,
                lastModified: Date.now(),
            });
        } catch (e) {
            toastServiceError(e);
        }
    }
};

const onSubmit = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    setLoading(true);

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewQueueById', params: { id } });
    };

    try {
        const formData = new FormData();
        formData.append('name', formState.name);
        formData.append('strategy', formState.strategy);
        formData.append('isActive', formState.isActive.toString());
        formState.users.forEach((user, index) => {
            formData.append(`users[${index}]`, user.toString());
        });
        const hasFileChanged = (file.value?.name ?? null) !== oldFileName.value;

        if (file.value && hasFileChanged) formData.append('audio', file.value);

        if (audioRemoved.value) formData.append('audioRemoved', 'true');

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await QueueService.update(id, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await QueueService.create(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        await fetchQueue(Number(route.params.id));
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiAudioUpload = _resolveComponent("UiAudioUpload")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('queue.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListQueue' } }),
          (
                            (props.action == _unref(FormAction).CREATE && _ctx.$can(_unref(Permission).CREATE_QUEUE)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_QUEUE))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, { class: "grid grid-cols-1 gap-4 md:grid-cols-3" }, {
      default: _withCtx(() => [
        _createVNode(_component_UiTextInput, {
          modelValue: _unref(v$).name.$model,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
          name: "name",
          type: "text",
          label: _ctx.$t('queue.labels.name'),
          errors: _unref(v$).name.$errors,
          onBlur: _unref(v$).name.$touch
        }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
        _createElementVNode("div", null, [
          _createElementVNode("label", _hoisted_3, _toDisplayString(_ctx.$t('queue.labels.strategy')), 1),
          _createVNode(_component_UiMultiselect, {
            id: "strategy",
            name: "strategy",
            modelValue: formState.strategy,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.strategy) = $event)),
            primitive: "",
            options: strategyOptions.value
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", null, [
          _createElementVNode("label", _hoisted_4, _toDisplayString(_ctx.$t('queue.labels.users')), 1),
          _createVNode(_component_UiMultiselect, {
            id: "users",
            name: "users",
            label: "name",
            "value-prop": "id",
            modelValue: formState.users,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.users) = $event)),
            multiple: "",
            primitive: "",
            options: usersOptions.value
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_UiCheckboxInput, {
            modelValue: _unref(v$).isActive.$model,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
            name: "isActive",
            label: _ctx.$t('queue.labels.isActive'),
            class: "h-6"
          }, null, 8, ["modelValue", "label"])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, { class: "flex flex-col space-y-2" }, {
      default: _withCtx(() => [
        _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t('queue.labels.audioUpload')), 1),
        _createVNode(_component_UiAudioUpload, {
          file: file.value,
          "onUpdate:file": _cache[4] || (_cache[4] = ($event: any) => ((file).value = $event)),
          "text-rule": _ctx.$t('queue.labels.uploadRules'),
          errors: _unref(v$).fileAudio.$errors
        }, null, 8, ["file", "text-rule", "errors"]),
        (_unref(v$).fileAudio.$errors && _unref(v$).fileAudio.$errors.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).fileAudio.$errors, (error) => {
                return (_openBlock(), _createElementBlock("p", {
                  key: error.$uid
                }, _toDisplayString(error.$message), 1))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (audioPreview.value)
      ? (_openBlock(), _createBlock(_component_UiPanel, {
          key: 0,
          class: "space-y-2"
        }, {
          default: _withCtx(() => [
            _createElementVNode("label", _hoisted_8, _toDisplayString(_ctx.$t('queue.labels.audioPreview')), 1),
            _createElementVNode("audio", _hoisted_9, [
              _createElementVNode("source", { src: audioPreview.value }, null, 8, _hoisted_10),
              _cache[5] || (_cache[5] = _createTextVNode(" Your browser does not support the audio element. ", -1))
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})